import { useEffect, useState } from "react";
import { GlobalTable } from "../../../global/components/GlobalTable";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Col, Input, Row, Select, Modal } from "antd";
import { InvitationColumns } from "./Columns";
import ConfirmModal from "../../../global/components/ConfirmModal";
import useDelete from "../../../global/hooks/useDelete";
import {
    openConfirmNotification,
    openErrorNotification,
} from "../../../global/utils/toast";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import { CloseOutlined, DeleteOutlined, SendOutlined } from "@ant-design/icons";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { HiArrowPath } from "react-icons/hi2";
import useGet from "../../../global/hooks/useGet";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import { getDealerNo, getOemNo, hasRole } from "../../../global/hooks/getUserRole";
import SentIcon from "../../../assets/images/vectors/SentIcon";
import { SendInvitationForm } from "../../Account/Components/SendInvitationForm";
import CommonButton from "../../../global/components/CommonButton";

const initialFilterState = {
    InviteNo: "",
    Email: "",
    AccountType: null,
    OemNo: null,
    DealerNo: null,
    CustomerNo: null,
};

export const Invitation = () => {
    const [hoveredRow, setHoveredRow] = useState(null);
    const [listData, setListData] = useState([]);
    const [params, setParams] = useSearchParams({
        limit: 10,
        sort_by: "InviteNo",
        order: "desc",
    });
    const modParams = { ...Object.fromEntries([...params?.entries()]) } || {};
    const isDealerAccount = hasRole(7, 8, 9);

    const initialPagination = {
        current: parseInt(modParams.page) || 1,
        pageSize: parseInt(modParams.limit) || 10,
        total: 0,
    };
    const [pagination, setPagination] = useState(initialPagination);
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [filter, setFilter] = useState(initialFilterState);
    const [showModal, setShowModal] = useState(false);
    const navigate = useNavigate();

    // Send Invitation modal — exact same state shape as Account page
    const [isSendInviteOpen, setIsSendInviteOpen] = useState(false);
    const [isOpenModal, setIsOpenModal] = useState(false);
    const [modalType, setModalType] = useState("");

    const query = new URLSearchParams(params);
    query.delete("pageSize");
    query.delete("total");
    query.delete("current");
    if (isDealerAccount) {
        const dn = getDealerNo();
        if (dn != null && dn !== "") query.set("DealerNo", String(dn));
    }
    const updatedQuery = query.toString();
    const url = updatedQuery
        ? `${process.env.REACT_APP_API_URL}/api/accounts/invites/?${updatedQuery}`
        : `${process.env.REACT_APP_API_URL}/api/accounts/invites/`;

    const { data, loading, refetch } = useGet(url);

    const optionUrl = `${process.env.REACT_APP_API_URL}/api/accounts/options/`;
    const { data: optionData } = useGet(optionUrl);

    const modifiedAccountTypes = getModifiedResponse(optionData, "roles");
    const modifiedOems = getModifiedResponse(optionData, "oems");
    const modifiedCustomers = getModifiedResponse(optionData, "customers");

    // Dealer options: same cascade logic as Device
    const dealerOptionsUrl =
        filter?.OemNo
            ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${filter.OemNo}/dealers/`
            : !hasRole(1, 2, 3) && getOemNo()
                ? `${process.env.REACT_APP_API_URL}/api/dealers/oems/${getOemNo()}/dealers/`
                : null;
    const { data: dealerOptions } = useGet(dealerOptionsUrl);
    const modifiedDealers = getModifiedResponse(dealerOptions, null);

    const deleteUrl =
        selectedRowKeys.length > 1
            ? `${process.env.REACT_APP_API_URL}/api/accounts/invites/bulk-delete/`
            : `${process.env.REACT_APP_API_URL}/api/accounts/invites/${selectedRowKeys}/`;

    const {
        data: deletedData,
        error: deletedDataError,
        deleteData,
        deleteMultiple,
    } = useDelete(deleteUrl);

    useEffect(() => {
        const matchedFilter = {};
        for (const key in initialFilterState) {
            if (modParams[key] !== undefined) {
                matchedFilter[key] = modParams[key];
            }
        }
        setFilter((prev) => ({ ...prev, ...matchedFilter }));
    }, []);

    const paginationConfig = {
        current: pagination.current,
        pageSize: pagination.pageSize,
        total: data?.count,
        showSizeChanger: true,
        pageSizeOptions: [10, 20, 50, 100],
        showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
        onChange: (page, pageSize) => {
            setPagination({ ...pagination, current: page, pageSize });
            setParams({
                ...Object.fromEntries([...params.entries()]),
                limit: pageSize,
                page,
            });
        },
    };

    const onSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const rowSelection = {
        selectedRowKeys,
        onChange: onSelectChange,
        fixed: true,
    };

    const handleFilterChange = (e) => {
        const inputName = e.target.name;
        const inputValue = e.target.value.replace(/['""`''""]/g, "");
        setFilter({ ...filter, [inputName]: inputValue });
    };

    const handleFilter = () => {
        const filterDict = {};
        for (const key in filter) {
            if (
                filter[key] !== null &&
                filter[key] !== "" &&
                filter[key] !== undefined
            ) {
                filterDict[key] = filter[key];
            }
        }
        const isEmpty = JSON.stringify(filterDict) === "{}";
        if (isEmpty) {
            handleReset();
        } else {
            setPagination((prev) => ({ ...prev, current: 1 }));
            setParams({
                limit: 10,
                sort_by: "InviteNo",
                order: "desc",
                page: 1,
                ...filterDict,
            });
            refetch();
        }
    };

    const handleReset = () => {
        setFilter({ ...initialFilterState });
        setPagination({ current: 1, pageSize: 10, total: 0 });
        setParams({ limit: 10, sort_by: "InviteNo", order: "desc" });
        refetch();
    };

    const onDeleteConfirm = async () => {
        try {
            if (selectedRowKeys.length === 1) {
                await deleteData();
            } else {
                await deleteMultiple({ ids: selectedRowKeys });
            }
        } catch (error) {
            openErrorNotification("Something went wrong.");
        }
        setShowModal(false);
        setSelectedRowKeys([]);
    };

    useEffect(() => {
        if (deletedData && !deletedDataError) {
            openConfirmNotification("Success");
            refetch();
        } else if (deletedDataError) {
            openErrorNotification(
                deletedDataError?.response?.data?.message || "Something went wrong"
            );
        }
    }, [deletedData, deletedDataError]);

    useEffect(() => {
        if (data && !loading) {
            const newData = data?.data?.map((el) => ({ ...el, key: el?.InviteNo }));
            setListData(newData);
        }
    }, [data, loading]);

    const breadcrumbItems = [{ label: "Invitation", href: "/invitation" }];

    const onSearch = (value) => { };

    return (
        <div>
            <div className="pb-4 -mt-4 pt-[6px]">
                <CommonBreadcrumb breadcrumbItems={breadcrumbItems} >
                    <CommonButton
                        buttonsData={[
                            {
                                label: " Close",
                                icon: (
                                    <CloseOutlined className="!text-[14px] font-medium" />
                                ),
                                onClick: () => {
                                    navigate(`/account`);
                                },
                            },
                        ]}
                    />
                </CommonBreadcrumb>

            </div>
            <div className="p-4 bg-white rounded-[4px] border border-gray-200">
                <div className="mb-4">
                    {/* ── Filters ── */}
                    <div className="mt-2">
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                                <Input
                                    className="input_text_3 mr10 h-full"
                                    placeholder="No"
                                    value={filter.InviteNo}
                                    onChange={handleFilterChange}
                                    name="InviteNo"
                                />
                            </Col>
                            <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                                <Input
                                    className="input_text_3 mr10 h-full"
                                    placeholder="Email"
                                    value={filter.Email}
                                    onChange={handleFilterChange}
                                    name="Email"
                                />
                            </Col>
                            <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                                <Select
                                    showSearch
                                    placeholder="Account Type"
                                    optionFilterProp="label"
                                    value={
                                        filter.AccountType || filter.AccountType === 0
                                            ? Number(filter.AccountType)
                                            : undefined
                                    }
                                    onChange={(value) =>
                                        setFilter((prev) => ({ ...prev, AccountType: value ?? null }))
                                    }
                                    onSearch={onSearch}
                                    options={modifiedAccountTypes}
                                    style={{ width: "100%" }}
                                    allowClear
                                />
                            </Col>

                            {/* OEM — only for system roles 1,2,3 — same as Device */}
                            {hasRole(1, 2, 3) && (
                                <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                                    <Select
                                        showSearch
                                        placeholder="OEM"
                                        optionFilterProp="label"
                                        value={
                                            filter.OemNo || filter.OemNo === 0
                                                ? Number(filter.OemNo)
                                                : undefined
                                        }
                                        onChange={(value) =>
                                            setFilter((prev) => ({
                                                ...prev,
                                                OemNo: value ?? null,
                                                DealerNo: null,
                                            }))
                                        }
                                        onSearch={onSearch}
                                        options={modifiedOems}
                                        style={{ width: "100%" }}
                                        allowClear
                                    />
                                </Col>
                            )}

                            {/* Dealer — visible for system + oem roles (1-6), same as Device */}
                            {hasRole(1, 2, 3, 4, 5, 6) && (
                                <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                                    <Select
                                        showSearch
                                        placeholder="Dealer"
                                        optionFilterProp="label"
                                        value={
                                            filter.DealerNo ? Number(filter.DealerNo) : undefined
                                        }
                                        onChange={(value) =>
                                            setFilter((prev) => ({ ...prev, DealerNo: value ?? null }))
                                        }
                                        onSearch={onSearch}
                                        options={modifiedDealers}
                                        style={{ width: "100%" }}
                                        allowClear
                                        // OEM roles (4,5,6) can pick dealer freely; system roles need OEM first
                                        disabled={
                                            hasRole(4, 5, 6)
                                                ? false
                                                : filter.OemNo === undefined ||
                                                filter.OemNo === null ||
                                                filter.OemNo === ""
                                        }
                                    />
                                </Col>
                            )}

                            {/* Customer — hidden for customer roles (9-12), same as Device */}
                            {!hasRole(9, 10, 11, 12) && (
                                <Col xs={24} sm={24} md={12} lg={8} xl={6} className="!h-[32px]">
                                    <Select
                                        showSearch
                                        placeholder="Customer"
                                        optionFilterProp="label"
                                        value={
                                            filter.CustomerNo ? Number(filter.CustomerNo) : undefined
                                        }
                                        onChange={(value) =>
                                            setFilter((prev) => ({ ...prev, CustomerNo: value ?? null }))
                                        }
                                        onSearch={onSearch}
                                        options={modifiedCustomers}
                                        style={{ width: "100%" }}
                                        allowClear
                                        // Dealer roles (7,8,9) can pick customer freely; others need Dealer first
                                        disabled={
                                            hasRole(7, 8, 9)
                                                ? false
                                                : filter.DealerNo === undefined ||
                                                filter.DealerNo === null ||
                                                filter.DealerNo === ""
                                        }
                                    />
                                </Col>
                            )}
                        </Row>
                    </div>

                    {/* ── Action buttons ── */}
                    <div className="w-full overflow-x-auto">
                        <div className="flex justify-between my-4 flex-nowrap min-w-[610px]">
                            <div className="flex items-center gap-2">
                                <Button
                                    type="primary"
                                    className="w-[104px] h-[30px] font-medium"
                                    onClick={handleFilter}
                                >
                                    <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
                                    Apply
                                </Button>
                                <Button className="reset-button" onClick={handleReset}>
                                    <HiArrowPath className="!text-[14px] font-medium" />
                                    Reset
                                </Button>
                                <Button
                                    type="primary"
                                    disabled={selectedRowKeys?.length === 0}
                                    className="w-[104px] h-[30px] font-medium"
                                    onClick={() => setShowModal(true)}
                                >
                                    <DeleteOutlined />
                                    Delete
                                </Button>
                                <Button
                                    type="primary"
                                    className="min-w-[104px] h-[30px] font-medium"
                                    onClick={() => setIsSendInviteOpen(true)}
                                >
                                    <SentIcon />
                                    Send Invitation
                                </Button>
                            </div>
                        </div>
                    </div>

                    <ConfirmModal
                        showModal={showModal}
                        setShowModal={setShowModal}
                        onDeleteConfirm={onDeleteConfirm}
                        setSelectedRowKeys={setSelectedRowKeys}
                    />

                    {/* ── Send Invitation modal — 100% same as Account page ── */}
                    <Modal
                        afterClose={() => setModalType("")}
                        destroyOnClose={true}
                        title={
                            <div className="flex items-center bg-gray-200 p-2 rounded-md">
                                <SendOutlined className="text-[15px] text-black ml-1" />
                                <span className="text-[14px] font-[Poppins] text-black ml-2">
                                    {isOpenModal ? `Add ${modalType}` : "Send Invitation"}
                                </span>
                            </div>
                        }
                        open={isSendInviteOpen}
                        closable={false}
                        onCancel={() => setIsSendInviteOpen(false)}
                        footer={null}
                        width={580}
                        centered
                    >
                        <SendInvitationForm
                            setIsModalOpen={(val) => {
                                setIsSendInviteOpen(val);
                                if (!val) refetch();
                            }}
                            isOpenModal={isOpenModal}
                            setIsOpenModal={setIsOpenModal}
                            setModalType={setModalType}
                            modalType={modalType}
                            isModalOpen={isSendInviteOpen}
                        />
                    </Modal>

                    <GlobalTable
                        columns={InvitationColumns(
                            hoveredRow,
                            setShowModal,
                            setSelectedRowKeys,
                            setParams,
                            params
                        )}
                        dataSource={listData}
                        rowSelection={rowSelection}
                        pagination={paginationConfig}
                        onRow={(record, rowIndex) => ({
                            onMouseEnter: () => setHoveredRow(rowIndex),
                            onMouseLeave: () => setHoveredRow(null),
                        })}
                        loading={loading}
                    />
                </div>
            </div>
        </div>
    );
};